import {
  setShowOverlay,
  setInRequest,
  setCallFrom,
  setMessages,
  setCallConnected,
} from "../store/actions";
import store from "./../store/store";
import * as wss from "../utils/wss";
import Peer from "simple-peer";
import { fetchTURNCredentials, getTURNIceServers } from "./turn";
import RecordRTCPromisesHandler from "recordrtc";
import { uploadMediaFile } from "./api";
import { v4 as uuidv4 } from "uuid";
import { stopRingtone } from "./ringtone";

const hdreadyConstraint = {
  audio: true,
  video: {
    width: "640",
    height: "480",
  },
};

const defaultConstraint = {
  audio: true,
  video: {
    width: "480",
    height: "360",
  },
};

let localStream;

// ─── Tab/browser close cleanup ───────────────────────────────────────────────
const handlePageHide = () => {
  cleanupOnExit();
};

const handleBeforeUnload = () => {
  cleanupOnExit();
};

const cleanupOnExit = () => {
  try {
    Object.keys(peers).forEach((socketId) => {
      try {
        peers[socketId].destroy();
      } catch (_) {}
    });

    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }

    wss.disconnect();
  } catch (_) {}
};

window.addEventListener("pagehide", handlePageHide);
window.addEventListener("beforeunload", handleBeforeUnload);

// ─────────────────────────────────────────────────────────────────────────────

export const getLocalPreviewAndInitRoomConnection = async (
  isRoomHost,
  identity,
  rooomId = null,
  onlyAudio,
  fromJid = "",
  toJid = ""
) => {
  try {
    await fetchTURNCredentials();

    const { previewAudioEnabled, previewVideoEnabled } = store.getState();

    navigator.mediaDevices
      .getUserMedia(defaultConstraint)
      .then((stream) => {
        localStream = stream;

        const audioTrack = localStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = previewAudioEnabled;
        }

        const videoTrack = localStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = onlyAudio ? false : previewVideoEnabled;
        }

        showLocalVideoPreview(localStream, identity);
        store.dispatch(setShowOverlay(false));
        const cofferEmail = store.getState().cofferEmail;
        isRoomHost
          ? wss.createNewRoom(identity, onlyAudio, fromJid, toJid, cofferEmail)
          : wss.joinRoom(identity, rooomId, onlyAudio, fromJid, cofferEmail);
      })
      .catch((err) => {
        console.log(
          `Error occurred when trying to get access to local Stream -> ${err}`
        );
      });
  } catch (error) {
    console.log(error);
  }
};

let peers = {};
let streams = [];
let meetingId;
let recorder;
let remoteRecorder = [];
let chatUsers = [];
let streamIds = [];
let streamToParticipantMap = {};

export const startSaveLocalStream = async () => {
  remoteRecorder = [];
  streamIds = [];
  meetingId = uuidv4();
  recorder = new RecordRTCPromisesHandler(localStream, {
    type: "audio",
  });
  recorder.startRecording();
};

export const getLocalStream = () => localStream;

export const leaveRoom = () => {
  cleanupAllSpeakingDetection();

  console.log("[webRTCHandler] leaveRoom called");

  if (localStream) {
    localStream.getTracks().forEach((track) => {
      track.stop();
    });
    console.log("[webRTCHandler] local tracks stopped");
    localStream = null;
  }

  disconnectUser();
  console.log("[webRTCHandler] leaveRoom completed");
};

export const stopSaveLocalStream = async () => {
  return new Promise((resolve, reject) => {
    store.dispatch(setShowOverlay(true));
    recorder.stopRecording(async function () {
      let blobContent = recorder.getBlob();
      const file = new File([blobContent], `${uuidv4()}.wav`, {
        type: "audio/wav",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("meetingid", meetingId);
      formData.append("jid", store.getState().cofferEmail);
      formData.append("name", store.getState().identity);

      await uploadMediaFile(formData);
      store.dispatch(setShowOverlay(false));
      resolve("Uploaded and Added to Queue");
    });
  });
};

export const startSaveRemoteStream = async () => {
  console.log(`Remote Recording started.. ${streams.length}`);
  streams.forEach((stream) => {
    saveStream(stream);
  });
};

const saveStream = (stream) => {
  let obj = new RecordRTCPromisesHandler(stream, {
    type: "audio",
  });
  remoteRecorder.push(obj);
  streamIds.push(stream.id);
  remoteRecorder[remoteRecorder.length - 1].startRecording();
};

export const stopSaveRemoteStream = async () => {
  return new Promise((resolve, reject) => {
    store.dispatch(setShowOverlay(true));
    console.log(`Remote Recording Stopped.. ${remoteRecorder.length}`);
    let idx = 0;

    remoteRecorder.forEach((recorder, index) => {
      const streamId = streamIds[index];
      const participantInfo = streamToParticipantMap[streamId];

      let fromJid = "";
      let fromName = "";

      if (participantInfo) {
        fromJid = participantInfo.cofferEmail;
        fromName = participantInfo.identity;
      }

      recorder.stopRecording(async function () {
        let blobContent = recorder.getBlob();
        const file = new File([blobContent], `${uuidv4()}.wav`, {
          type: "audio/wav",
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("meetingid", meetingId);
        formData.append("jid", fromJid);
        formData.append("name", fromName);

        await uploadMediaFile(formData);
        idx++;
        if (idx >= remoteRecorder.length) {
          store.dispatch(setShowOverlay(false));
        }
        resolve("Uploaded and Added to Queue");
      });
    });
  });
};

const getConfiguration = () => {
  const turnIceServers = getTURNIceServers();
  if (turnIceServers) {
    return {
      iceServers: [
        {
          urls: "stun:stun.l.google.com:19302",
        },
        ...turnIceServers,
      ],
    };
  } else {
    console.warn("Using only STUN Server...");
    return {
      iceServers: [
        {
          urls: "stun:stun.l.google.com:19302",
        },
      ],
    };
  }
};

const messengerChannel = "messenger";

export const prepareNewPeerConnection = (connUserSocketId, isInitiator) => {
  const configuration = getConfiguration();
  peers[connUserSocketId] = new Peer({
    initiator: isInitiator,
    config: configuration,
    stream: localStream,
    channelName: messengerChannel,
  });

  peers[connUserSocketId].on("signal", (data) => {
    const signalData = {
      signal: data,
      connUserSocketId: connUserSocketId,
    };
    wss.signalPeerData(signalData);
  });

  peers[connUserSocketId].on("stream", (stream) => {
    localStorage.removeItem("expectCallTimeout");
    localStorage.removeItem("missedCallTimeout");
    localStorage.removeItem("missedCallName");
    stopRingtone();

    store.dispatch(setCallConnected(true));

    addStream(stream, connUserSocketId);
    streams = [...streams, stream];

    const participants = store.getState().participants;
    const participant = participants.find((p) => p.socketId === connUserSocketId);
    if (participant && stream.id) {
      streamToParticipantMap[stream.id] = {
        cofferEmail: participant.cofferEmail,
        identity: participant.identity,
        socketId: connUserSocketId,
      };
    }

    if (store.getState().isRoomHost && store.getState().isRecordingOn) {
      saveStream(stream);
    }
  });

  peers[connUserSocketId].on("data", (data) => {
    try {
      const messageData = JSON.parse(data);

      if (messageData.type === "camera_state") {
        console.log(
          `📡 Received camera state from ${messageData.identity}: ${
            messageData.cameraEnabled ? "ON" : "OFF"
          }`
        );
        handleRemoteCameraStateChange(
          connUserSocketId,
          messageData.cameraEnabled,
          messageData.identity
        );
      } else if (messageData.type === "mic_state") {
        console.log(
          `📡 Received mic state from ${messageData.identity}: ${
            messageData.micEnabled ? "UNMUTED" : "MUTED"
          }`
        );
        handleRemoteMicStateChange(
          connUserSocketId,
          messageData.micEnabled,
          messageData.identity
        );
      } else {
        appendNewMessage(messageData);
      }
    } catch (err) {
      console.error("Error parsing peer data:", err);
    }
  });

  peers[connUserSocketId].on("error", (err) => {
    const msg = err?.message || "";
    if (
      msg.includes("User-Initiated Abort") ||
      msg.includes("Close called") ||
      msg.includes("Connection failed")
    ) {
      console.warn(`[peer ${connUserSocketId}] connection closed:`, msg);
      return;
    }
    console.error(`[peer ${connUserSocketId}] unexpected error:`, err);
  });
};

// ─── Track remote mic state per socket ───────────────────────────────────────
const remoteMicState = {};

const handleRemoteCameraStateChange = (connUserSocketId, cameraEnabled, identity) => {
  const videoContainer = document.getElementById(connUserSocketId);
  if (!videoContainer) {
    console.warn(`Video container not found for ${connUserSocketId}`);
    return;
  }

  const videoElement = videoContainer.querySelector("video");
  const existingPlaceholder = videoContainer.querySelector(".video_off_placeholder");
  const nameLabel = videoContainer.querySelector(".participant_name_label");

  const isMicMuted = remoteMicState[connUserSocketId] === false;

  if (cameraEnabled) {
    if (existingPlaceholder) {
      videoContainer.removeChild(existingPlaceholder);
    }
    if (videoElement) {
      videoElement.style.display = "block";
      videoElement.play().catch((e) => console.warn("play() failed:", e));
    }
  } else {
    if (videoElement) {
      videoElement.style.display = "none";
    }
    if (!existingPlaceholder) {
      const placeholder = getVideoOffPlaceholder(identity, isMicMuted);
      if (nameLabel) {
        videoContainer.insertBefore(placeholder, nameLabel);
      } else if (videoElement) {
        videoContainer.insertBefore(placeholder, videoElement);
      } else {
        videoContainer.appendChild(placeholder);
      }
    }
  }
};

const handleRemoteMicStateChange = (connUserSocketId, micEnabled, identity) => {
  remoteMicState[connUserSocketId] = micEnabled;

  const videoContainer = document.getElementById(connUserSocketId);
  if (!videoContainer) {
    console.warn(`Video container not found for ${connUserSocketId}`);
    return;
  }

  const placeholder = videoContainer.querySelector(".video_off_placeholder");
  const videoElement = videoContainer.querySelector("video");
  const isVideoVisible = videoElement && videoElement.style.display !== "none";

  if (placeholder) {
    const existingIndicator = placeholder.querySelector(".mic_muted_indicator");
    if (!micEnabled && !existingIndicator) {
      const mutedIndicator = createMutedIndicator();
      placeholder.appendChild(mutedIndicator);
    } else if (micEnabled && existingIndicator) {
      placeholder.removeChild(existingIndicator);
    }
  } else if (isVideoVisible) {
    const existingIndicator = videoContainer.querySelector(".mic_muted_indicator");
    if (!micEnabled && !existingIndicator) {
      const mutedIndicator = createMutedIndicator();
      mutedIndicator.classList.add("mic_muted_indicator_over_video");
      videoContainer.appendChild(mutedIndicator);
    } else if (micEnabled && existingIndicator) {
      videoContainer.removeChild(existingIndicator);
    }
  }
};

export const handleSignalingData = (data) => {
  peers[data.connUserSocketId].signal(data.signal);
};

export const removePeerConnection = (data) => {
  const { socketId } = data;

  cleanupSpeakingDetection(socketId);

  delete remoteMicState[socketId];

  if (window.remoteTrackPollers && window.remoteTrackPollers[socketId]) {
    clearInterval(window.remoteTrackPollers[socketId]);
    delete window.remoteTrackPollers[socketId];
  }

  const videoContainer = document.getElementById(socketId);
  const videoEle = document.getElementById(`${socketId}-video`);

  if (videoEle && videoContainer) {
    const tracks = videoEle.srcObject.getTracks();
    tracks.forEach((t) => {
      t.stop();
    });
    videoEle.srcObject = null;
    videoContainer.removeChild(videoEle);
    videoContainer.parentNode.removeAttribute("class");
    videoContainer.parentNode.className = "";
    const total_participants = store.getState().participants.length - 1;

    if (total_participants <= 6)
      videoContainer.parentNode.classList.add(`grid-container-${total_participants}`);
    else videoContainer.parentNode.classList.add(`grid-container-infinite2`);

    videoContainer.parentNode.removeChild(videoContainer);

    if (peers[socketId]) {
      peers[socketId].destroy();
    }
  }
};

export const sendLoggedInInfo = (jid, name) => {
  wss.loggedIn(jid, name);
  store.dispatch(setInRequest(false));
  store.dispatch(setCallFrom(""));
};

export const sendCallRequestAlert = (fromUserName) => {
  store.dispatch(setInRequest(true));
  store.dispatch(setCallFrom(fromUserName));
};

export const disconnectUser = () => {
  wss.disconnect();
};

/////////////UI Stuff here //////////////////////

const getInitials = (name) => {
  if (!name) return "?";

  const trimmedName = name.trim();
  const cleanName = trimmedName.replace(/[^a-zA-Z\s]/g, "").trim();

  if (!cleanName) return "?";

  const parts = cleanName.split(/\s+/);

  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  } else if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }

  return "?";
};

const getColorFromName = (name) => {
  const googleMeetColors = [
    "#9C27B0", "#673AB7", "#3F51B5", "#2196F3", "#03A9F4",
    "#00BCD4", "#009688", "#4CAF50", "#8BC34A", "#FF9800",
    "#FF5722", "#795548", "#607D8B", "#E91E63", "#F44336",
  ];

  if (!name) return googleMeetColors[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const index = Math.abs(hash) % googleMeetColors.length;
  return googleMeetColors[index];
};

const showLocalVideoPreview = (stream, custIdentity) => {
  const videosContainer = document.getElementById("videos_portal");
  videosContainer.classList.add("grid-container-1");
  const videoContainer = document.createElement("div");
  videoContainer.classList.add("video_container_wrapper");

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.srcObject = stream;

  videoElement.onloadedmetadata = () => {
    videoElement.play();
  };

  const videoTrack = stream.getVideoTracks()[0];
  const isVideoEnabled = videoTrack && videoTrack.enabled;

  const identity = store.getState().identity || custIdentity;

  const audioTrack = stream.getAudioTracks()[0];
  const isMicMuted = audioTrack ? !audioTrack.enabled : false;

  if (store.getState().connectOnlyWithAudio || !isVideoEnabled) {
    videoContainer.appendChild(getVideoOffPlaceholder(identity, isMicMuted));
    videoElement.style.display = "none";
  }

  const nameLabel = document.createElement("div");
  nameLabel.classList.add("participant_name_label");
  nameLabel.textContent = identity || "Guest";
  videoContainer.appendChild(nameLabel);

  videoContainer.appendChild(videoElement);
  videosContainer.appendChild(videoContainer);

  initializeLocalSpeakingDetection(stream);
};

const addStream = (stream, connUserSocketId) => {
  const videosContainer = document.getElementById("videos_portal");
  videosContainer.classList.remove("grid-container-1");
  videosContainer.className = "";
  if (store.getState().participants.length <= 6) {
    videosContainer.classList.add(`grid-container-${store.getState().participants.length}`);
  } else videosContainer.classList.add(`grid-container-infinite2`);

  const videoContainer = document.createElement("div");
  videoContainer.id = connUserSocketId;
  videoContainer.classList.add("video_container_wrapper");

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.srcObject = stream;
  videoElement.playsInline = true;
  videoElement.id = `${connUserSocketId}-video`;

  videoElement.onloadedmetadata = () => {
    videoElement.play();
  };

  videoElement.addEventListener("click", () => {
    if (videoElement.classList.contains("full_screen")) {
      videoElement.classList.remove("full_screen");
    } else {
      videoElement.classList.add("full_screen");
    }
  });

  const participants = store.getState().participants;
  const participant = participants.find((p) => p.socketId === connUserSocketId);

  if (participant) {
    const participantCopy = {
      ...participant,
      mediaStreamId: stream.id,
    };
    chatUsers.push(participantCopy);

    const videoTrack = stream.getVideoTracks()[0];
    const isVideoDisabled = !videoTrack || !videoTrack.enabled;

    if (participant?.onlyAudio || isVideoDisabled) {
      videoContainer.appendChild(getVideoOffPlaceholder(participant.identity));
      videoElement.style.display = "none";
    }

    const nameLabel = document.createElement("div");
    nameLabel.classList.add("participant_name_label");
    nameLabel.textContent = participant.identity || "Guest";
    videoContainer.appendChild(nameLabel);
  }

  videoContainer.appendChild(videoElement);
  videosContainer.appendChild(videoContainer);

  initializeRemoteSpeakingDetection(stream, connUserSocketId);
  sendInitialCameraState(connUserSocketId);
  sendInitialMicState(connUserSocketId);
};

const createMutedIndicator = () => {
  const indicator = document.createElement("div");
  indicator.classList.add("mic_muted_indicator");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute(
    "d",
    "M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"
  );
  path.setAttribute("fill", "white");

  svg.appendChild(path);
  indicator.appendChild(svg);

  return indicator;
};

export const sendInitialCameraState = (socketId) => {
  setTimeout(() => {
    const videoTrack = localStream?.getVideoTracks()[0];
    const cameraEnabled = videoTrack ? videoTrack.enabled : false;

    const identity = store.getState().identity;
    const cameraStateMessage = {
      type: "camera_state",
      identity: identity,
      cameraEnabled: cameraEnabled,
      timestamp: new Date().toJSON(),
    };

    try {
      peers[socketId].send(JSON.stringify(cameraStateMessage));
    } catch (err) {
      console.error("Failed to send initial camera state:", err);
    }
  }, 1000);
};

export const sendInitialMicState = (socketId) => {
  setTimeout(() => {
    const audioTrack = localStream?.getAudioTracks()[0];
    const micEnabled = audioTrack ? audioTrack.enabled : false;

    const identity = store.getState().identity;
    const micStateMessage = {
      type: "mic_state",
      identity: identity,
      micEnabled: micEnabled,
      timestamp: new Date().toJSON(),
    };

    try {
      peers[socketId].send(JSON.stringify(micStateMessage));
    } catch (err) {
      console.error("Failed to send initial mic state:", err);
    }
  }, 1200);
};

const updateMutedIndicator = (container, isMuted) => {
  const existingIndicator = container.querySelector(".mic_muted_indicator");

  if (isMuted && !existingIndicator) {
    const mutedIndicator = createMutedIndicator();
    container.appendChild(mutedIndicator);
  } else if (!isMuted && existingIndicator) {
    container.removeChild(existingIndicator);
  }
};

const getVideoOffPlaceholder = (identity = "", isMuted = false) => {
  const placeholderContainer = document.createElement("div");
  placeholderContainer.classList.add("video_off_placeholder");

  const avatarColor = getColorFromName(identity);
  placeholderContainer.style.background = `linear-gradient(135deg, ${avatarColor}66 0%, ${avatarColor}99 100%)`;

  const avatarCircle = document.createElement("div");
  avatarCircle.classList.add("avatar_circle");
  avatarCircle.style.backgroundColor = avatarColor;

  const initials = document.createElement("span");
  initials.classList.add("avatar_initials");
  initials.textContent = getInitials(identity);

  avatarCircle.appendChild(initials);
  placeholderContainer.appendChild(avatarCircle);

  if (isMuted) {
    const mutedIndicator = createMutedIndicator();
    placeholderContainer.appendChild(mutedIndicator);
  }

  return placeholderContainer;
};

const getParticipantLabel = (identity = "") => {
  const labelContainer = document.createElement("span");
  labelContainer.classList.add("video-overlay");

  const label = document.createElement("p");
  label.classList.add("label_only_audio_text");
  label.textContent = identity || "Guest";

  labelContainer.appendChild(label);
  return labelContainer;
};

///////Buttons Logic ///////////////

export const toggleMic = (isMuted) => {
  const newState = isMuted ? true : false;
  localStream.getAudioTracks()[0].enabled = newState;

  console.log(`🎤 Mic ${newState ? "unmuted" : "muted"}`);

  const videosContainer = document.getElementById("videos_portal");
  if (videosContainer) {
    const videoContainers = videosContainer.querySelectorAll(".video_container_wrapper");
    if (videoContainers.length > 0) {
      const localContainer = videoContainers[0];
      const placeholder = localContainer.querySelector(".video_off_placeholder");
      if (placeholder) {
        updateMutedIndicator(placeholder, !newState);
      }
    }
  }

  broadcastMicState(newState);
};

export const toggleCamera = async (isDiabled) => {
  const newState = isDiabled ? true : false;
  let videoTrack = localStream.getVideoTracks()[0];

  if (videoTrack) {
    videoTrack.enabled = newState;
  } else if (newState) {
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: "480", height: "360" },
      });
      const newVideoTrack = videoStream.getVideoTracks()[0];
      localStream.addTrack(newVideoTrack);

      const replacePromises = [];
      for (let socketId in peers) {
        const sender = peers[socketId]._pc
          .getSenders()
          .find((s) => s.track?.kind === "video");
        if (sender) {
          replacePromises.push(sender.replaceTrack(newVideoTrack));
        }
      }
      await Promise.all(replacePromises);

      const videosContainer = document.getElementById("videos_portal");
      const firstContainer = videosContainer?.querySelectorAll(".video_container_wrapper")[0];
      const videoEl = firstContainer?.querySelector("video");
      if (videoEl) videoEl.srcObject = localStream;

      videoTrack = newVideoTrack;
    } catch (error) {
      console.error("Error enabling camera:", error);
      alert("Failed to enable camera. Please check permissions.");
      return;
    }
  } else {
    return;
  }

  const videosContainer = document.getElementById("videos_portal");
  const videoContainers = videosContainer.querySelectorAll(".video_container_wrapper");

  if (videoContainers.length > 0) {
    const localVideoContainer = videoContainers[0];
    const videoElement = localVideoContainer.querySelector("video");
    const existingPlaceholder = localVideoContainer.querySelector(".video_off_placeholder");
    const nameLabel = localVideoContainer.querySelector(".participant_name_label");

    if (newState) {
      if (existingPlaceholder) {
        localVideoContainer.removeChild(existingPlaceholder);
      }
      if (videoElement) {
        videoElement.style.display = "block";
      }
    } else {
      if (videoElement) {
        videoElement.style.display = "none";
      }
      if (!existingPlaceholder) {
        const identity = store.getState().identity;
        const audioTrack = localStream?.getAudioTracks()[0];
        const isMicMuted = audioTrack ? !audioTrack.enabled : false;
        const placeholder = getVideoOffPlaceholder(identity, isMicMuted);

        if (nameLabel) {
          localVideoContainer.insertBefore(placeholder, nameLabel);
        } else if (videoElement) {
          localVideoContainer.insertBefore(placeholder, videoElement);
        } else {
          localVideoContainer.appendChild(placeholder);
        }
      }
    }
  }

  broadcastCameraState(newState);
};

const broadcastCameraState = (cameraEnabled) => {
  const identity = store.getState().identity;
  const cameraStateMessage = {
    type: "camera_state",
    identity: identity,
    cameraEnabled: cameraEnabled,
    timestamp: new Date().toJSON(),
  };

  console.log(`📡 Broadcasting camera state: ${cameraEnabled ? "ON" : "OFF"}`);

  const stringifiedMessage = JSON.stringify(cameraStateMessage);

  for (let socketId in peers) {
    try {
      peers[socketId].send(stringifiedMessage);
      console.log(`  ✅ Sent to peer ${socketId}`);
    } catch (err) {
      console.error(`  ❌ Failed to send to peer ${socketId}:`, err);
    }
  }
};

const broadcastMicState = (micEnabled) => {
  const identity = store.getState().identity;
  const micStateMessage = {
    type: "mic_state",
    identity: identity,
    micEnabled: micEnabled,
    timestamp: new Date().toJSON(),
  };

  console.log(`📡 Broadcasting mic state: ${micEnabled ? "UNMUTED" : "MUTED"}`);

  const stringifiedMessage = JSON.stringify(micStateMessage);

  for (let socketId in peers) {
    try {
      peers[socketId].send(stringifiedMessage);
      console.log(`  ✅ Sent mic state to peer ${socketId}`);
    } catch (err) {
      console.error(`  ❌ Failed to send to peer ${socketId}:`, err);
    }
  }
};

export const handleRemoteVideoTrackState = (stream, connUserSocketId) => {
  const videoTrack = stream.getVideoTracks()[0];
  if (!videoTrack) return;

  const checkTrackState = () => {
    const videoContainer = document.getElementById(connUserSocketId);
    if (!videoContainer) return;

    const videoElement = videoContainer.querySelector("video");
    const existingPlaceholder = videoContainer.querySelector(".video_off_placeholder");
    const nameLabel = videoContainer.querySelector(".participant_name_label");

    const participants = store.getState().participants;
    const participant = participants.find((p) => p.socketId === connUserSocketId);
    const identity = participant?.identity || "Guest";

    if (videoTrack.enabled) {
      if (existingPlaceholder) {
        videoContainer.removeChild(existingPlaceholder);
      }
      if (videoElement) {
        videoElement.style.display = "block";
      }
    } else {
      if (videoElement) {
        videoElement.style.display = "none";
      }
      if (!existingPlaceholder) {
        const placeholder = getVideoOffPlaceholder(identity);
        if (nameLabel) {
          videoContainer.insertBefore(placeholder, nameLabel);
        } else if (videoElement) {
          videoContainer.insertBefore(placeholder, videoElement);
        } else {
          videoContainer.appendChild(placeholder);
        }
      }
    }
  };

  checkTrackState();

  let lastEnabledState = videoTrack.enabled;
  const pollInterval = setInterval(() => {
    if (!videoTrack || videoTrack.readyState === "ended") {
      clearInterval(pollInterval);
      return;
    }
    if (videoTrack.enabled !== lastEnabledState) {
      lastEnabledState = videoTrack.enabled;
      checkTrackState();
    }
  }, 500);

  if (!window.remoteTrackPollers) {
    window.remoteTrackPollers = {};
  }
  window.remoteTrackPollers[connUserSocketId] = pollInterval;
};

///////// Messages ////////////////

const appendNewMessage = (messageData) => {
  const messages = store.getState().messages;
  store.dispatch(setMessages([...messages, messageData]));
};

export const sendMessageUsingDataChannel = (messageContent) => {
  const identity = store.getState().identity;
  const messageDateTime = new Date();
  const localMessageData = {
    content: messageContent,
    identity,
    messageCreatedByMe: true,
    sentDateTime: messageDateTime.toJSON(),
  };
  appendNewMessage(localMessageData);

  const messageData = {
    content: messageContent,
    identity,
    sentDateTime: messageDateTime.toJSON(),
  };

  const stringifiedMessageData = JSON.stringify(messageData);

  for (let socketId in peers) {
    try {
      peers[socketId].send(stringifiedMessageData);
    } catch (err) {
      console.log(err);
    }
  }
};

// ─── SPEAKING DETECTION ───────────────────────────────────────────────────────

const audioContexts = {};
const audioAnalyzers = {};

// ✅ FIX: Store animation frame IDs so we can cancel them on cleanup
let localAnimationFrameId = null;
const remoteAnimationFrameIds = {};

let localAudioContext = null;
let localAudioAnalyzer = null;
let localAudioSource = null;

export const initializeLocalSpeakingDetection = (stream) => {
  try {
    if (localAudioContext) {
      localAudioContext.close();
    }

    localAudioContext = new (window.AudioContext || window.webkitAudioContext)();

    localAudioAnalyzer = localAudioContext.createAnalyser();
    localAudioAnalyzer.fftSize = 512;
    localAudioAnalyzer.smoothingTimeConstant = 0.8;

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) {
      console.warn("No audio track found for local speaking detection");
      return;
    }

    const mediaStream = new MediaStream([audioTrack]);
    localAudioSource = localAudioContext.createMediaStreamSource(mediaStream);

    localAudioSource.connect(localAudioAnalyzer);

    monitorLocalSpeaking();
  } catch (error) {
    console.error("Error initializing local speaking detection:", error);
  }
};

const monitorLocalSpeaking = () => {
  if (!localAudioAnalyzer) return;

  const bufferLength = localAudioAnalyzer.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  let isSpeaking = false;
  const SPEAKING_THRESHOLD = 45;
  const SPEAKING_DEBOUNCE = 300;
  let lastSpeakingTime = 0;

  const checkSpeaking = () => {
    // ✅ FIX: Stop the loop if analyzer was cleaned up
    if (!localAudioAnalyzer) return;

    try {
      localAudioAnalyzer.getByteFrequencyData(dataArray);
    } catch (err) {
      // ✅ FIX: Stop silently on error — prevents app crash
      console.warn("Local audio analyzer error, stopping loop:", err);
      return;
    }

    const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    const now = Date.now();

    if (average > SPEAKING_THRESHOLD) {
      lastSpeakingTime = now;
      if (!isSpeaking) {
        isSpeaking = true;
      }
      updateLocalSpeakingUI(true, average);
    } else if (isSpeaking && now - lastSpeakingTime > SPEAKING_DEBOUNCE) {
      isSpeaking = false;
      updateLocalSpeakingUI(false, 0);
    }

    // ✅ FIX: Store frame ID so it can be cancelled in cleanup
    localAnimationFrameId = requestAnimationFrame(checkSpeaking);
  };

  checkSpeaking();
};

const updateLocalSpeakingUI = (speaking, volume) => {
  const videosContainer = document.getElementById("videos_portal");
  if (!videosContainer) return;

  const videoContainers = videosContainer.querySelectorAll(".video_container_wrapper");
  if (videoContainers.length === 0) return;

  const localContainer = videoContainers[0];

  if (speaking) {
    if (volume > 60) {
      localContainer.classList.add("speaking-loud");
      localContainer.classList.remove("speaking");
    } else {
      localContainer.classList.add("speaking");
      localContainer.classList.remove("speaking-loud");
    }

    const nameLabel = localContainer.querySelector(".participant_name_label");
    if (nameLabel) {
      nameLabel.classList.add("speaking");

      if (!nameLabel.querySelector(".speaking-indicator-icon")) {
        const speakingIcon = document.createElement("span");
        speakingIcon.className = "speaking-indicator-icon";
        nameLabel.appendChild(speakingIcon);
      }
    }

    const placeholder = localContainer.querySelector(".video_off_placeholder");
    if (placeholder) {
      placeholder.classList.add("speaking");
    }
  } else {
    localContainer.classList.remove("speaking", "speaking-loud");

    const nameLabel = localContainer.querySelector(".participant_name_label");
    if (nameLabel) {
      nameLabel.classList.remove("speaking");
      const speakingIcon = nameLabel.querySelector(".speaking-indicator-icon");
      if (speakingIcon) {
        speakingIcon.remove();
      }
    }

    const placeholder = localContainer.querySelector(".video_off_placeholder");
    if (placeholder) {
      placeholder.classList.remove("speaking");
    }
  }
};

export const initializeRemoteSpeakingDetection = (stream, socketId) => {
  try {
    if (audioContexts[socketId]) {
      audioContexts[socketId].close();
      delete audioContexts[socketId];
      delete audioAnalyzers[socketId];
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContexts[socketId] = audioContext;

    const analyzer = audioContext.createAnalyser();
    analyzer.fftSize = 512;
    analyzer.smoothingTimeConstant = 0.8;
    audioAnalyzers[socketId] = analyzer;

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) {
      console.warn(`No audio track found for ${socketId}`);
      return;
    }

    const mediaStream = new MediaStream([audioTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);

    source.connect(analyzer);

    monitorRemoteSpeaking(socketId);
  } catch (error) {
    console.error(`Error initializing speaking detection for ${socketId}:`, error);
  }
};

const monitorRemoteSpeaking = (socketId) => {
  const analyzer = audioAnalyzers[socketId];
  if (!analyzer) return;

  const bufferLength = analyzer.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  let isSpeaking = false;
  const SPEAKING_THRESHOLD = 30;
  const SPEAKING_DEBOUNCE = 300;
  let lastSpeakingTime = 0;

  const checkSpeaking = () => {
    // ✅ FIX: Stop the loop if analyzer was cleaned up
    if (!audioAnalyzers[socketId]) return;

    try {
      analyzer.getByteFrequencyData(dataArray);
    } catch (err) {
      // ✅ FIX: Stop silently on error — prevents app crash & redirect
      console.warn(`Remote audio analyzer error for ${socketId}, stopping loop:`, err);
      return;
    }

    const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    const now = Date.now();

    if (average > SPEAKING_THRESHOLD) {
      lastSpeakingTime = now;
      if (!isSpeaking) {
        isSpeaking = true;
      }
      updateRemoteSpeakingUI(socketId, true, average);
    } else if (isSpeaking && now - lastSpeakingTime > SPEAKING_DEBOUNCE) {
      isSpeaking = false;
      updateRemoteSpeakingUI(socketId, false, 0);
    }

    // ✅ FIX: Store frame ID so it can be cancelled in cleanup
    remoteAnimationFrameIds[socketId] = requestAnimationFrame(checkSpeaking);
  };

  checkSpeaking();
};

const updateRemoteSpeakingUI = (socketId, speaking, volume) => {
  const container = document.getElementById(socketId);
  if (!container) return;

  if (speaking) {
    if (volume > 60) {
      container.classList.add("speaking-loud");
      container.classList.remove("speaking");
    } else {
      container.classList.add("speaking");
      container.classList.remove("speaking-loud");
    }

    const nameLabel = container.querySelector(".participant_name_label");
    if (nameLabel) {
      nameLabel.classList.add("speaking");
      if (!nameLabel.querySelector(".speaking-indicator-icon")) {
        const speakingIcon = document.createElement("span");
        speakingIcon.className = "speaking-indicator-icon";
        nameLabel.appendChild(speakingIcon);
      }
    }

    const placeholder = container.querySelector(".video_off_placeholder");
    if (placeholder) {
      placeholder.classList.add("speaking");
    }
  } else {
    container.classList.remove("speaking", "speaking-loud");

    const nameLabel = container.querySelector(".participant_name_label");
    if (nameLabel) {
      nameLabel.classList.remove("speaking");
      const speakingIcon = nameLabel.querySelector(".speaking-indicator-icon");
      if (speakingIcon) {
        speakingIcon.remove();
      }
    }

    const placeholder = container.querySelector(".video_off_placeholder");
    if (placeholder) {
      placeholder.classList.remove("speaking");
    }
  }
};

export const cleanupSpeakingDetection = (socketId) => {
  // ✅ FIX: Cancel animation frame before closing audio context
  if (remoteAnimationFrameIds[socketId]) {
    cancelAnimationFrame(remoteAnimationFrameIds[socketId]);
    delete remoteAnimationFrameIds[socketId];
  }

  if (audioContexts[socketId]) {
    audioContexts[socketId].close();
    delete audioContexts[socketId];
    delete audioAnalyzers[socketId];
  }
};

export const cleanupAllSpeakingDetection = () => {
  // ✅ FIX: Cancel local animation frame first to stop the loop immediately
  if (localAnimationFrameId) {
    cancelAnimationFrame(localAnimationFrameId);
    localAnimationFrameId = null;
  }

  if (localAudioContext) {
    localAudioContext.close();
    localAudioContext = null;
    localAudioAnalyzer = null;
    localAudioSource = null;
  }

  // ✅ FIX: Cancel all remote animation frames before closing contexts
  Object.keys(remoteAnimationFrameIds).forEach((socketId) => {
    cancelAnimationFrame(remoteAnimationFrameIds[socketId]);
    delete remoteAnimationFrameIds[socketId];
  });

  Object.keys(audioContexts).forEach((socketId) => {
    audioContexts[socketId].close();
  });

  Object.keys(audioContexts).forEach((k) => delete audioContexts[k]);
  Object.keys(audioAnalyzers).forEach((k) => delete audioAnalyzers[k]);

  console.log("Cleaned up all speaking detection");
};